/**
 * Multi-pet: one subscription line per dog, set up on a single product page.
 *
 * Each dog is a DISTINCT kcal product (e.g. frischfutter-0474 vs frischfutter-0763),
 * so this module fetches each dog's own product JSON (/products/{handle}.js) using
 * the `_product_handle` line-item property carried in the `_i` payload, and offers
 * that dog's own portions (V/H), delivery cycles and live price. Selections are
 * written back into the `_i` payload (localStorage `product_payload`) so the existing
 * add-to-cart (welcome-set.js) drops one correctly-configured subscription line per
 * dog into the cart.
 *
 * Activates ONLY when the payload has more than one item; single-dog pages keep
 * the original product form untouched.
 *
 * Nothing can be ordered until every dog is on the page. If a dog's product cannot be
 * loaded, the order stays blocked, with a message naming the dog and a reload button
 * (block()).
 *
 * PRESENTATION. Markup and copy are Liquid: snippets/fresh-pack.liquid prints them as
 * one inert <template> (page layout, dog accordion card, "Gleich wie" row, recipe tile,
 * summary rows, bottom sheet) and this file clones and fills it. Styles are
 * _dev/assets/sass/components/snippets/_fresh-pack.scss, all under `html.fresh-pack`,
 * which sections/fresh-product.liquid sets during parse from the same
 * `items.length > 1` test made here.
 *
 *   >= 992px   [ banner, heading, 1 Kaufart, 2 Dein Rudel ]  [ sticky summary + CTA ]
 *              [ benefits                                 ]
 *   <  992px   one column; the price in the fixed bar opens the breakdown in a sheet
 *
 * What is sold (sellability, variants, selling plans, the payload, the prices) is
 * decided by the logic below and does not depend on the layout.
 */
(function () {
  'use strict';

  var RECIPE_ORDER = ['CG', 'GS', 'RT', 'WS'];
  // Only used if the page does not print `[data-fresh-discounts]`. The real values
  // are theme settings (`global_discount_percentage` / `renewal_discount_percentage`).
  var DISCOUNT_FALLBACK = { first: 30, renewal: 10 };
  // How long a dog's two requests may take before it counts as not loaded (see block()).
  // Well past the loading cover's 10s cap in sections/fresh-product.liquid.
  var LOAD_TIMEOUT_MS = 15000;

  // ── payload helpers ─────────────────────────────────────────────────────────
  function decodeBase64Utf8(b64) {
    var raw = b64;
    try {
      raw = decodeURIComponent(b64);
    } catch (_) {}
    var bin = atob(raw);
    var bytes = Uint8Array.from(bin, function (c) {
      return c.charCodeAt(0);
    });
    return new TextDecoder('utf-8').decode(bytes);
  }

  function encodeBase64Utf8(str) {
    var bytes = new TextEncoder().encode(str);
    var bin = '';
    for (var i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
  }

  function rawPayloadString() {
    // Prefer the fresh _i straight from the URL: avoids a race with
    // standard-modern.js's storeProductPayload and any stale localStorage value.
    var m = window.location.search.match(/[?&]_i=([^&]+)/);
    if (m && m[1]) return m[1];
    return localStorage.getItem('product_payload');
  }

  function readPayload() {
    try {
      var raw = rawPayloadString();
      if (!raw) return null;
      return JSON.parse(decodeBase64Utf8(raw));
    } catch (e) {
      console.warn('[multi-pet-plans] payload decode failed', e);
      return null;
    }
  }

  function writePayload(payload) {
    try {
      localStorage.setItem('product_payload', encodeBase64Utf8(JSON.stringify(payload)));
    } catch (e) {
      /* non-fatal */
    }
  }

  // ── copy ────────────────────────────────────────────────────────────────────
  /*
   * Every word on screen comes from snippets/fresh-pack.liquid: the section settings the
   * single-dog page shows (so a Theme Editor change reaches both views) and the
   * `products.fresh_pack.*` locale keys for words only a pack needs. Locale placeholders
   * arrive as `[name]`; section settings use their own `{price}` / `{days}` tokens. Both
   * are filled here, by split/join so a "$" in a price is never read as a pattern.
   */
  var CONFIG = null;

  function templateRoot() {
    return document.getElementById('fresh-pack-templates');
  }

  function config() {
    if (CONFIG) return CONFIG;
    var parsed = {};
    var root = templateRoot();
    var node = root && root.content ? root.content.querySelector('[data-pack-config]') : null;
    if (node) {
      try {
        parsed = JSON.parse(node.textContent);
      } catch (err) {
        console.warn('[multi-pet-plans] unreadable copy in snippets/fresh-pack.liquid', err);
      }
    }
    /*
     * Liquid's `t` filter HTML-escapes a translation, and inside a JSON <script> the
     * entities are not decoded, so "couldn't" arrived as "couldn&#39;t" and was shown like
     * that through textContent. Decoded once here; every string is set as text, never as
     * markup, so nothing in a translation can become HTML.
     */
    var strings = {};
    Object.keys(parsed.strings || {}).forEach(function (key) {
      var value = parsed.strings[key];
      if (value == null) return;
      // A detached <textarea> parses its content as text: no element is ever created.
      var area = document.createElement('textarea');
      area.innerHTML = String(value);
      strings[key] = area.value;
    });
    CONFIG = {
      strings: strings,
      pensions: parsed.pensions || {},
      recipes: parsed.recipes || {},
      lang: parsed.lang || document.documentElement.lang || 'de',
      country: parsed.country || (window.Shopify && window.Shopify.country) || 'DE',
    };
    return CONFIG;
  }

  function t(key, values) {
    var text = config().strings[key];
    if (text == null) return '';
    text = String(text);
    Object.keys(values || {}).forEach(function (name) {
      var value = String(values[name]);
      text = text.split('[' + name + ']').join(value).split('{' + name + '}').join(value);
    });
    return text;
  }

  // ── formatting ──────────────────────────────────────────────────────────────
  /*
   * "73,79 €": the format the rest of this page uses — snippets/fresh-money.liquid on
   * the server, formatMoney() in fresh-product-form-modern.liquid live — so a pack's
   * prices look like a single dog's. CHF keeps that formatter's own style. (This used to
   * drop the space and follow the country's locale: "130,97€", and "€71,83" on .at.)
   */
  function formatMoney(cents) {
    var amount = cents / 100;
    if (config().country === 'CH') {
      return new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' })
        .format(amount)
        .replace('.', ',');
    }
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  }

  /** "3 Wochen", "1 Woche", or "30 Tage" when not whole weeks — as on the cycle buttons. */
  function periodLabel(days) {
    var n = parseInt(days, 10);
    if (n > 0 && n % 7 === 0) {
      var weeks = n / 7;
      return weeks === 1 ? t('weekSingular') : weeks + ' ' + t('weekPlural');
    }
    return n + ' ' + t('dayPlural');
  }

  /** "21 Tage Futter" — the second line of a cycle button. */
  function cycleDaysLabel(days) {
    return t('cycleDays', { days: parseInt(days, 10) });
  }

  function recipesLabel(count) {
    return count + ' ' + (count === 1 ? t('recipeSingular') : t('recipePlural'));
  }

  function pensionLabel(code) {
    return config().pensions[code] || code;
  }

  /** The dog's name as PetCon stored it, trimmed ("Wuffi " happens), else "Hund 2". */
  function petName(item, index) {
    var raw = item && item.properties && item.properties._pet_name;
    var name = raw ? String(raw).trim() : '';
    return name || t('dogFallback', { number: index + 1 }) || 'Hund ' + (index + 1);
  }

  /** "Molly, Baf und Rex" in the page's language. */
  function joinNames(names) {
    try {
      if (window.Intl && Intl.ListFormat) {
        return new Intl.ListFormat(config().lang, { style: 'long', type: 'conjunction' }).format(names);
      }
    } catch (err) {
      /* unknown locale: fall through */
    }
    if (names.length < 2) return names.join('');
    return names.slice(0, -1).join(', ') + ' ' + t('and') + ' ' + names[names.length - 1];
  }

  /*
   * One heading for the pack, at every width: the names for two or three dogs, "dein
   * Rudel" for more, or when the names would not fit on a phone in two lines.
   */
  var HEADING_MAX_NAMES = 3;
  var HEADING_MAX_CHARS = 30;

  function headingText(names) {
    var list = names.filter(Boolean);
    if (list.length >= 2 && list.length <= HEADING_MAX_NAMES) {
      var joined = joinNames(list);
      if (joined.length <= HEADING_MAX_CHARS) return t('headingNames', { names: joined });
    }
    return t('headingPack');
  }

  /*
   * The first-delivery and renewal discounts, as printed by the form. Display only
   * — the discount the customer is actually charged is applied by the code at
   * checkout — but it has to agree with the single-dog card on the same page, so it
   * is read from there rather than held as a constant here.
   */
  function discounts() {
    var node = document.querySelector('[data-fresh-discounts]');
    if (!node) return DISCOUNT_FALLBACK;
    try {
      var parsed = JSON.parse(node.textContent);
      return {
        first: typeof parsed.first === 'number' ? parsed.first : DISCOUNT_FALLBACK.first,
        renewal: typeof parsed.renewal === 'number' ? parsed.renewal : DISCOUNT_FALLBACK.renewal,
      };
    } catch (err) {
      return DISCOUNT_FALLBACK;
    }
  }

  // ── recipe parsing ──────────────────────────────────────────────────────────
  function recipesFromOption1(option1) {
    var codes = (option1 || '').match(/.{1,2}/g) || [];
    return codes.filter(function (c) {
      return RECIPE_ORDER.indexOf(c) !== -1;
    });
  }

  function recipesKey(selected) {
    return RECIPE_ORDER.filter(function (c) {
      return selected.indexOf(c) !== -1;
    }).join('');
  }

  function findVariant(product, key, pension, cycle) {
    return (product.variants || []).find(function (v) {
      return v.option1 === key && v.option2 === pension && String(v.option3) === String(cycle);
    });
  }

  /*
   * What this product actually sells, narrowed by the rest of the current
   * selection. All three used to be computed ONCE from every variant, which made
   * the chips wrong in both directions: a cycle offered for one recipe mix was
   * shown for every mix, and the cycle list was additionally filtered to a
   * hardcoded ['14','28'] — so a product selling 21 and 35 (measured:
   * frischfutter-0474 sells Vollpension at 21 and 35 only) offered no cycle at
   * all, while one selling 14 and 28 looked correct by luck.
   *
   * Clicks were then silently swallowed by an `if (!findVariant(...)) return;`
   * guard, so an unavailable chip looked identical to an available one and simply
   * did nothing.
   */
  /*
   * Cycles this product sells at `pension` for `key`, gated by
   * `custom.available_for_sale`.
   *
   * A variant existing is NOT the same as it being sellable: on this catalogue
   * EVERY product/mix/pension combination carries more cycles than it sells, so
   * offering the ones that exist is wrong everywhere, not in edge cases.
   *
   * `availability` is the `{"V-14": true}` map the product page prints, because
   * `/products/{handle}.js` omits metafields. The Liquid that prints it says as
   * much: "`product.variants | json` omits metafields, so the client needs this
   * map." A cell must be explicitly `true` — the same test the native form makes:
   * `isSellable(p, c) { return freshAvailability[p + '-' + c] === true; }`.
   *
   * No map, no cycles. There is deliberately nothing invented here: falling back to
   * variant existence is what offered the unsellable ones (0774 CGGSRTWS sells
   * Vollpension at 14/28/35 and still carries a 42 variant; 0474 carries five and
   * sells two), and falling back to a single guessed cell is the same mistake
   * pointing the other way. `loadPageData` validates the page it read and retries
   * once, then logs an error, so an empty list means the data could not be read.
   */
  function cyclesFor(product, key, pension, availability) {
    if (!availability) return [];
    return uniq(
      (product.variants || [])
        .filter(function (v) {
          if (v.option1 !== key || v.option2 !== pension) return false;
          return availability[v.option2 + '-' + v.option3] === true;
        })
        .map(function (v) {
          return String(v.option3);
        }),
    ).sort(function (a, b) {
      return parseInt(a, 10) - parseInt(b, 10);
    });
  }

  /*
   * Same gate for the pension axis: a pension with no sellable cycle for this mix
   * must not be offered either.
   */
  function pensionsForSellable(product, key, availability) {
    return pensionsFor(product, key).filter(function (pension) {
      return cyclesFor(product, key, pension, availability).length > 0;
    });
  }

  /*
   * Tell the full-page loader (sections/fresh-product.liquid) that the panel is
   * drawn. It covers the page from parse time so the single-dog view never shows
   * for a pack; this is what ends it, from the first draw and from the fallback
   * path.
   *
   * `done()` rather than removing the node: the cover holds itself up for a minimum
   * time, and reaching past that API would bypass the floor and put the flicker
   * back. Removing directly only as a fallback, for a page whose section predates
   * the API. The section also arms its own hard cap, so no single failure can leave
   * the page behind it.
   */
  function liftCover() {
    var api = window.freshPackLoading;
    if (api && typeof api.done === 'function') {
      api.done();
      return;
    }
    var cover = document.getElementById('fresh-pack-loading');
    if (cover && cover.parentNode) cover.parentNode.removeChild(cover);
  }

  /** One of the product page's `<script type="application/json">` blobs. */
  function readJsonNode(root, selector) {
    var node = root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      return null;
    }
  }
  /*
   * The two JSON blobs the product page prints that `/products/{handle}.js` does
   * not carry: sellability per `pension-cycle`, and per-recipe durations keyed by
   * variant id.
   *
   * Fetched with `?variant=` so Liquid resolves THIS dog's variant — the
   * availability map is built from `product.variants | where: 'option1',
   * current_recipe`, so without it the map would describe whichever mix the page
   * defaults to rather than the dog's.
   */
  /*
   * The availability map covers ONE recipe mix — `product.variants | where:
   * 'option1', current_recipe`, and `current_recipe` comes from
   * `product.selected_variant`. So a map only belongs to this dog if the document
   * actually resolved this dog's variant. The form prints what it resolved as
   * `<input name="id">`; checking it is what stops a map for the wrong product or
   * the wrong mix being used as though it were right.
   */
  function readPageDataFrom(root, variantId) {
    var resolved = root.querySelector('[name="id"]');
    if (!resolved || String(resolved.value) !== String(variantId)) {
      throw new Error(
        'resolved variant ' + (resolved ? resolved.value : 'none') + ', wanted ' + variantId,
      );
    }
    var availability = readJsonNode(root, '[data-fresh-availability]');
    if (!availability) throw new Error('no [data-fresh-availability]');
    /*
     * The dog's calorie figure, from the page that belongs to it. It is NOT the
     * number in the handle: `frischfutter-0474` prints 476, because the heading
     * reads `product.metafields.custom.fresh_kcal` rather than the handle. Taking
     * it from the same document costs nothing and keeps each dog's panel agreeing
     * with that dog's own product page.
     */
    var kcalEl = root.querySelector('#selectedVariantLabel');
    return {
      availability: availability,
      days: readJsonNode(root, '[data-recipe-variant-days]'),
      kcal: kcalEl ? kcalEl.textContent.trim() : null,
    };
  }
  function fetchPageData(handle, variantId) {
    var url = '/products/' + handle + '?variant=' + encodeURIComponent(variantId);
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status + ' for ' + url);
        return r.text();
      })
      .then(function (html) {
        return readPageDataFrom(new DOMParser().parseFromString(html, 'text/html'), variantId);
      });
  }
  function loadPageData(handle, variantId) {
    /*
     * When this page already IS the dog's product at the dog's variant, the right
     * map is in the DOM — the same node the native script reads in `isSellable`.
     * Read it instead of re-fetching the page we are on. On the buy chain that is
     * always dog 1, whose `_product_handle` the URL was built from.
     */
    try {
      return Promise.resolve(readPageDataFrom(document, variantId));
    } catch (notThisPage) {
      /* another dog's product, or this page is on a different variant */
    }
    return fetchPageData(handle, variantId).catch(function (first) {
      return fetchPageData(handle, variantId).catch(function (second) {
        console.error(
          '[multi-pet-plans] could not read available_for_sale for ' +
            handle +
            ' @ ' +
            variantId +
            '; offering no cycles for this dog rather than unsellable ones',
          first,
          second,
        );
        return { availability: null, days: null, kcal: null };
      });
    });
  }

  function pensionsFor(product, key) {
    return uniq(
      (product.variants || [])
        .filter(function (v) {
          return v.option1 === key;
        })
        .map(function (v) {
          return v.option2;
        }),
    );
  }

  /** The cycle closest to `cycle` among `available`, preferring the smaller on a tie. */
  function nearestCycle(available, cycle) {
    if (!available.length) return null;
    if (available.indexOf(String(cycle)) !== -1) return String(cycle);

    var want = parseInt(cycle, 10);
    return available.slice().sort(function (a, b) {
      var left = parseInt(a, 10);
      var right = parseInt(b, 10);
      var byDistance = Math.abs(left - want) - Math.abs(right - want);
      return byDistance !== 0 ? byDistance : left - right;
    })[0];
  }

  /*
   * Plans whose DESCRIPTION marks them as not-for-normal-checkout. `Discount
   * Variant` is Loop's 20% cohort, handed out through its own retention and
   * win-back flows; selling one here would give away 20% at full checkout.
   * `Regular Variant` is preferred so a cadence that somehow has two candidates
   * resolves by rule instead of by whichever Shopify listed first.
   *
   * petcon's `findSellingPlanForCycle` and the chat widget's `findSellingPlan`
   * carry the identical rule. The theme had no exclusion at all, so it was the
   * one surface that could still sell a Discount plan.
   */
  var EXCLUDED_PLAN_MARKERS = ['AB Test', 'City', 'Discount Variant'];
  var PREFERRED_PLAN_MARKER = 'Regular Variant';

  /*
   * Whether a plan delivers on `cycle` DAYS.
   *
   * The plans were relabelled in Loop from days to weeks, so a name is now
   * "Lieferung alle 4 Wochen" and the old `indexOf('alle 28 Tage')` test matched
   * nothing — every lookup returned null, the line lost its selling plan, and the
   * dog could not be sold as a subscription at all. That is the same text-matching
   * break GW-2067 fixed in petcon and petcon-api by moving to the delivery policy.
   *
   * This surface cannot do that: the product JSON's selling plan carries no
   * interval. What it does carry is `options`, and those kept the DAY count
   * through the relabel ("28 Tage"), so they are checked first. The name is tried
   * second in both shapes, which is what makes this survive the next rename in
   * either direction.
   *
   * Anchored on a non-digit so cycle 7 cannot match "14 Tage" or "56 Tage".
   */
  function planMatchesCycle(plan, cycle) {
    var days = String(parseInt(cycle, 10));
    var dayRe = new RegExp('(^|\\D)' + days + '\\s*Tage?\\b');

    var opts = plan.options || [];
    for (var i = 0; i < opts.length; i++) {
      var opt = opts[i];
      var value = opt && typeof opt === 'object' ? opt.value : opt;
      if (dayRe.test(String(value || ''))) return true;
    }

    var name = String(plan.name || '');
    if (dayRe.test(name)) return true;

    if (parseInt(cycle, 10) % 7 === 0) {
      var weeks = String(parseInt(cycle, 10) / 7);
      if (new RegExp('(^|\\D)' + weeks + '\\s*Woche').test(name)) return true;
    }
    return false;
  }

  function sellingPlanForCycle(product, cycle) {
    var groups = product.selling_plan_groups || [];
    var candidates = [];

    for (var g = 0; g < groups.length; g++) {
      var plans = groups[g].selling_plans || [];
      for (var p = 0; p < plans.length; p++) {
        var plan = plans[p];
        if (!planMatchesCycle(plan, cycle)) continue;

        var description = String(plan.description || '');
        var excluded = EXCLUDED_PLAN_MARKERS.some(function (marker) {
          return description.indexOf(marker) !== -1;
        });
        if (!excluded) candidates.push(plan);
      }
    }

    if (!candidates.length) return null;

    var preferred = candidates.filter(function (plan) {
      return String(plan.description || '').indexOf(PREFERRED_PLAN_MARKER) !== -1;
    });
    return (preferred.length ? preferred : candidates)[0].id;
  }

  // ── design vocabulary ───────────────────────────────────────────────────────
  // The bowl photos of the Portion cards: the same files the single-dog cards show.
  var PENSION_IMG = {
    V: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/vollpension_meal.webp?v=1770905487',
    H: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/halbpension_meal.webp?v=1770905487',
  };

  // Recipe photos (the files snippets/fresh-recipe-rows.liquid uses). The names shown
  // next to them come from the section settings; `brand` is only the fallback.
  var RECIPE_META = {
    CG: { brand: 'Cowgenuss', img: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Cowgenuss_5.webp?v=1782817232' },
    GS: { brand: 'Gockelspeis', img: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Gockelspeis_5.webp?v=1782817231' },
    RT: { brand: 'Ringeltanz', img: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Ringeltanz_5.webp?v=1782817232' },
    WS: { brand: 'Wildschmaus', img: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Wildschmaus.webp?v=1786541591' },
  };

  /*
   * The dog tints: how many colours `$dog__tones` has in
   * _dev/assets/sass/base/_variables.scss (PetCon's list, same order). A dog's avatar
   * takes tone (pack position % DOG_TONE_COUNT) + 1, so it has the same colour here as in
   * the configurator; a fourth dog starts over at the first.
   */
  var DOG_TONE_COUNT = 3;

  /*
   * The tint class for a dog's paw avatar. `packIndex` is the dog's position in the `_i`
   * items, which PetCon builds from its own pets in order, so a dog has the colour it
   * had in the configurator.
   */
  function toneClass(packIndex) {
    return 'fresh-pack-avatar--tone-' + ((packIndex % DOG_TONE_COUNT) + 1);
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function uniq(arr) {
    return arr.filter(function (v, i) {
      return arr.indexOf(v) === i;
    });
  }

  /*
   * Sachet rows from the variant barcode — the same source fresh-recipe-rows.liquid
   * parses: triplets of CODE-COUNT-GRAMS, e.g. `CG-014-500-GS-014-600`. A recipe can
   * appear more than once (one triplet per pack size); zero-count triplets are padding.
   *
   * The barcode is in /products/{handle}.js, which is why a dog's pack can be shown at
   * all. The per-recipe DURATION ("reicht für 8 Tage") is not — it lives in variant
   * metafields the product JSON does not expose — so it comes from the dog's product
   * page instead (`days`, read by loadPageData).
   */
  function sachetRows(barcode) {
    var parts = String(barcode || '').split('-');
    var rows = [];
    for (var i = 0; i + 2 < parts.length; i += 3) {
      var code = parts[i];
      var count = parseInt(parts[i + 1], 10);
      var grams = parseInt(parts[i + 2], 10);
      if (!RECIPE_META[code] || !count || !grams) continue;
      rows.push({ code: code, count: count, grams: grams });
    }
    return rows;
  }

  /*
   * The sachet rows per recipe, in recipe order. A recipe in two pouch sizes is one tile
   * reading "8 × 800 g, 8 × 600 g", the way the single-dog card shows it.
   */
  function recipeGroups(barcode) {
    var byCode = {};
    sachetRows(barcode).forEach(function (row) {
      (byCode[row.code] = byCode[row.code] || []).push(row.count + ' × ' + row.grams + ' g');
    });
    return RECIPE_ORDER.filter(function (code) {
      return byCode[code];
    }).map(function (code) {
      return { code: code, sachets: byCode[code] };
    });
  }

  // ── templates ───────────────────────────────────────────────────────────────
  function clone(name) {
    var root = templateRoot();
    var source = root && root.content ? root.content.querySelector('[data-pack-tpl="' + name + '"]') : null;
    if (!source) return null;
    var node = document.importNode(source, true);
    node.removeAttribute('data-pack-tpl');
    return node;
  }

  function hook(root, name) {
    return root.querySelector('[data-pack-' + name + ']');
  }

  function hooks(root, name) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-pack-' + name + ']'));
  }

  /*
   * Nodes the page already has are MOVED into the layout, not copied, so they keep their
   * listeners and their Theme Editor content: the Kaufart fieldset (wired to the native
   * script), the add-to-cart bar, the delivery/guarantee lines, the benefits list, the
   * upsell mount. They never go back: a pack that cannot be shown blocks the order
   * (`block()`) instead of returning to the single-dog form.
   */
  function createMover() {
    return {
      move: function (node, target) {
        if (!node || !target || !node.parentNode) return false;
        target.appendChild(node);
        return true;
      },
    };
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function scrollByInstant(y) {
    try {
      window.scrollBy({ top: y, left: 0, behavior: 'instant' });
    } catch (err) {
      window.scrollBy(0, y);
    }
  }

  // ── orchestration ───────────────────────────────────────────────────────────
  function init() {
    var payload = readPayload();
    var count = payload && Array.isArray(payload.items) ? payload.items.length : 0;
    if (count <= 1) return;

    var formEl = document.querySelector('#add-to-cart-form');
    var layout = formEl ? clone('layout') : null;
    if (!formEl || !layout) {
      console.warn(
        '[multi-pet-plans] ' +
          (formEl ? 'snippets/fresh-pack.liquid is not on the page' : '#add-to-cart-form not found') +
          '; keeping the single-dog form',
      );
      document.documentElement.classList.remove('fresh-pack');
      liftCover();
      return;
    }

    /*
     * Hide the single-dog blocks with an ATTRIBUTE, not an inline style: the
     * page's script re-asserts `hidden` on every `[data-purchase-panel]` when the
     * purchase type changes, which would un-hide the native cycle step on Abo. An
     * inline `display:none` fought that and won, which is why the Kaufart switch
     * appeared to do nothing. `[data-multi-pet-hidden]` is `display:none
     * !important`, so it is stable against the toggling.
     */
    ['.fresh-step--pension', '.fresh-step--cycle', '.fresh-package', '.fresh-price-card'].forEach(
      function (selector) {
        var nodes = formEl.querySelectorAll(selector);
        if (!nodes.length) {
          console.warn('[multi-pet-plans] nothing matched', selector, '- form markup changed?');
        }
        nodes.forEach(function (node) {
          node.setAttribute('data-multi-pet-hidden', '1');
        });
      },
    );

    // ── mount ────────────────────────────────────────────────────────────────
    var mover = createMover();

    document.documentElement.classList.add('fresh-pack');
    var anchor = formEl.querySelector('[data-multi-pet-hidden]');
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(layout, anchor);
    else formEl.insertBefore(layout, formEl.firstChild);

    /*
     * Nothing reaches the cart until every dog is drawn. welcome-set.js adds one line per
     * `_i` item, so an add while the dogs are still loading, or with one of them missing,
     * would ship PetCon's lines without the customer having seen them. The button cannot
     * carry this alone: the form's own script re-enables it whenever dog 1's (hidden)
     * selection changes. So the submit itself is stopped, on `document` in the capture
     * phase, before any listener on the form (welcome-set.js among them) sees it.
     */
    var orderable = false;
    document.addEventListener(
      'submit',
      function (event) {
        if (orderable || event.target !== formEl) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true,
    );

    /*
     * Step 1 is the page's OWN Kaufart fieldset, moved rather than rebuilt.
     * Moving a node keeps its listeners, so the `purchase_type` radios stay wired
     * to the native script; rebuilding them would have made a second, unwired
     * group with the same `name`. It belongs first because it applies to the whole
     * cart and decides whether a delivery cycle is asked for at all.
     */
    var kaufart = formEl.querySelector('.fresh-step--purchase');
    var kaufartLegend = kaufart ? kaufart.querySelector('.fresh-step__label') : null;
    var kaufartShown = !!kaufart && !kaufart.hidden;
    if (kaufart) {
      mover.move(kaufart, hook(layout, 'slot-kaufart'));
      if (kaufartLegend && kaufartShown) kaufartLegend.textContent = '1 · ' + t('stepPurchase');

      /*
       * The Kaufart cards print per-day rates for the ONE variant the page was loaded
       * with — dog 1's. For a pack they show the pack's rates, in nodes of this module's
       * own, next to the native ones (hidden): the native `updatePrice()` keeps writing
       * its nodes (a `?discount=` code resolving late, a purchase-type switch), and with
       * separate nodes it can no longer overwrite the pack's figures.
       */
      [
        ['[data-subscription-price-per-day]', 'first'],
        ['[data-renewal-per-day]', 'later'],
        ['[data-one-time-price-per-day]', 'full'],
      ].forEach(function (kind) {
        kaufart.querySelectorAll(kind[0]).forEach(function (native) {
          var own = el('span', 'fresh-pack-pending', '—');
          own.setAttribute('data-pack-per-day', kind[1]);
          native.parentNode.insertBefore(own, native.nextSibling);
          native.hidden = true;
        });
      });
      // "für 2 Hunde zusammen" under each card's rates.
      kaufart.querySelectorAll('.fresh-choice__label').forEach(function (label) {
        var scope = el('span', 'fresh-pack-scope');
        scope.setAttribute('data-pack-scope', '');
        label.appendChild(scope);
      });
    }
    hook(layout, 'rudel-label').textContent = (kaufartShown ? '2' : '1') + ' · ' + t('rudel');

    // The add-to-cart bar: its own button and listeners, moved; its price area becomes
    // the opener of the per-dog breakdown.
    var bar = formEl.querySelector('.payment-buttons');
    var barButton = clone('bar');
    if (bar) {
      mover.move(bar, hook(layout, 'slot-bar'));
      bar.insertBefore(barButton, bar.querySelector('[data-add-to-cart]') || bar.firstChild);
    }

    mover.move(
      document.querySelector('.section-product-fresh .fresh-recommendation-product-content > .fresh-trust'),
      hook(layout, 'slot-trust'),
    );

    /*
     * The page's own "Deine Vorteile im Abo-Sparplan" ticks, moved out of the
     * hidden price card rather than rebuilt, so the wording stays whatever the
     * Theme Editor says. Taken from the subscription panel specifically; once it is
     * out of there the native `syncPurchasePanels()` no longer touches it, so this
     * module decides when it shows.
     */
    var benefitsEl =
      formEl.querySelector('[data-purchase-panel="subscription"] .fresh-product-gratis') ||
      formEl.querySelector('.fresh-price-card .fresh-product-gratis');
    var benefitsWrap = hook(layout, 'benefits');
    if (benefitsEl) mover.move(benefitsEl, hook(layout, 'slot-benefits'));

    // The upsell app's mount lived in the gallery column, which a pack does not show from
    // 992px. Under the configuration it shows from 768 to 991px; desktop leaves it out.
    mover.move(
      document.querySelector('.section-product-fresh .product-images .essential-upsell-cross-sell-block'),
      hook(layout, 'slot-upsell'),
    );

    /*
     * Desktop: gallery slide 1 as the banner above the heading (the image the gallery
     * already loaded, so no second download) and the rating row. Slides 2 and 3 are
     * not shown for a pack.
     */
    var mainImage = document.getElementById('mainImage');
    if (mainImage) {
      var bannerImg = hook(layout, 'banner-img');
      bannerImg.src = mainImage.currentSrc || mainImage.getAttribute('src');
      bannerImg.alt = mainImage.getAttribute('alt') || '';
      hook(layout, 'banner').hidden = false;
    }

    var rating = document.querySelector(
      '.section-product-fresh .fresh-recommendation-product-content > .fresh-recommendation-product-reviews',
    );
    if (rating) {
      var ratingCopy = rating.cloneNode(true);
      ratingCopy.classList.remove('d-none', 'd-lg-flex');
      hook(layout, 'rating').appendChild(ratingCopy);
    }

    var titleEl = hook(layout, 'title');
    function renderHeading(names) {
      titleEl.textContent = headingText(names);
    }
    renderHeading(payload.items.map(petName));

    var dogsHost = hook(layout, 'dogs');
    var asideSummary = layout.querySelector('[data-pack-summary] .fresh-pack-summary');
    hook(asideSummary, 'sum-eyebrow').id = 'fresh-pack-summary-title';

    var sheet = createSheet();

    /*
     * A dog that could not be loaded, or a pack that could not be drawn, blocks the order.
     * The order has to hold every dog PetCon sent, each as the customer saw and chose it,
     * so neither a pack without that dog nor the single-dog form is offered instead.
     * Everything that configures or orders is hidden (`.is-blocked`), the message names
     * the dogs, and its button reloads the page, which fetches every dog again.
     */
    function block(names) {
      orderable = false;
      dogs = [];
      if (sheet && sheet.open) closeSheet();
      layout.classList.remove('is-loading');
      layout.classList.add('is-blocked');

      var message = clone('blocked');
      if (message) {
        hook(message, 'blocked-title').textContent =
          names.length === 1
            ? t('blockedOne', { name: names[0] })
            : t('blockedMany', { names: joinNames(names) });
        hook(message, 'blocked-reload').addEventListener('click', function () {
          window.location.reload();
        });
        // Inserted rather than unhidden, so screen readers announce the alert.
        var head = hook(layout, 'head');
        head.parentNode.insertBefore(message, head.nextSibling);
      } else {
        console.error('[multi-pet-plans] snippets/fresh-pack.liquid has no blocked message');
      }
      liftCover();
    }

    /*
     * THE WAIT. Two products have to be fetched per dog before a single cycle or price
     * can be shown. The full-page loader normally covers it; this is what shows if that
     * cover hits its cap first. The names need no request (`_pet_name` is in `_i`), so
     * the cards are drawn with them straight away, at their final height, and only what
     * needs the network shimmers.
     */
    function renderLoading() {
      dogsHost.textContent = '';
      payload.items.forEach(function (item, index) {
        var ghost = clone('dog');
        ghost.classList.add('is-loading');
        hook(ghost, 'dog-toggle').disabled = true;
        // Already in the dog's final tint, so the colour does not change when the real
        // card replaces this one (`index` is the pack position: nothing is dropped yet).
        hook(ghost, 'dog-avatar').classList.add(toneClass(index));
        hook(ghost, 'dog-name').textContent = petName(item, index);
        hook(ghost, 'dog-summary').appendChild(el('span', 'fresh-pack-shimmer'));
        hook(ghost, 'dog-now').appendChild(el('span', 'fresh-pack-shimmer fresh-pack-shimmer--price'));
        dogsHost.appendChild(ghost);
      });

      hook(asideSummary, 'sum-eyebrow').textContent =
        readPurchaseType() === 'subscription' ? t('firstDelivery') : t('oneTimeTitle');
      var body = hook(asideSummary, 'rows-body');
      body.textContent = '';
      payload.items.forEach(function (item, index) {
        var row = clone('row');
        hook(row, 'row-avatar').classList.add(toneClass(index));
        hook(row, 'row-name').textContent = petName(item, index);
        hook(row, 'row-meta').appendChild(el('span', 'fresh-pack-shimmer'));
        hook(row, 'row-now').textContent = '—';
        hook(row, 'row-later').textContent = '—';
        body.appendChild(row);
      });
      hook(asideSummary, 'later-total').textContent = '—';
    }

    // ── state ────────────────────────────────────────────────────────────────
    var dogs = [];
    var purchaseType = 'subscription';

    /*
     * The purchase type is watched HERE rather than delegated to the native
     * `[data-purchase-panel]` mechanism. That mechanism captures its node list
     * once, inside `productForm()` on DOMContentLoaded; the dog cards do not exist
     * by then — they are built after the product fetches resolve.
     */
    function readPurchaseType() {
      var checked = formEl.querySelector('[name="purchase_type"]:checked');
      return checked && checked.value === 'one_time' ? 'one_time' : 'subscription';
    }

    formEl.querySelectorAll('[name="purchase_type"]').forEach(function (radio) {
      /*
       * Deferred by a frame on purpose. The page's own `productForm()` listens on
       * these same radios and calls `updatePrice()`. This module's listener is
       * registered first (`init()` runs before `productForm()`), so a frame later
       * this module draws last.
       */
      radio.addEventListener('change', function () {
        customerChanged = true;
        requestAnimationFrame(redraw);
      });
    });

    var handlers = {
      purchaseType: function () {
        return purchaseType;
      },
      onPension: function (index, pension) {
        dogs[index].pension = pension;
        // The pension changes which cycles this dog can take, so pull its cycle
        // into the new set rather than leaving it on a dead cell.
        var offered = cyclesFor(
          dogs[index].product,
          recipesKey(dogs[index].recipes),
          pension,
          dogs[index].availability,
        );
        var snapped = nearestCycle(offered, dogs[index].cycle);
        if (snapped) dogs[index].cycle = snapped;
        redraw();
      },
      onCycle: function (index, cycle) {
        dogs[index].cycle = cycle;
        redraw();
      },
    };

    /*
     * On Einzelkauf there is no cycle control, but a variant still carries a cycle
     * in its SKU, so one has to be chosen: the smallest the dog can take, which is
     * what the native one-time path does for a single dog.
     */
    function effectiveCycle(dog) {
      var offered = cyclesFor(dog.product, recipesKey(dog.recipes), dog.pension, dog.availability);
      if (!offered.length) return dog.cycle;
      if (purchaseType === 'one_time') return offered[0];
      return offered.indexOf(String(dog.cycle)) !== -1
        ? String(dog.cycle)
        : nearestCycle(offered, dog.cycle);
    }

    function resolve() {
      dogs.forEach(function (dog) {
        // The seeded pension comes from `_i` and can itself be unsellable, so it
        // is pulled onto a sellable one before the cycle is chosen.
        var sellablePensions = pensionsForSellable(dog.product, recipesKey(dog.recipes), dog.availability);
        if (sellablePensions.length && sellablePensions.indexOf(dog.pension) === -1) {
          dog.pension = sellablePensions[0];
        }
        dog.cycle = effectiveCycle(dog);
        dog.variant = findVariant(dog.product, recipesKey(dog.recipes), dog.pension, dog.cycle);
      });
    }

    function syncPayload() {
      var current = readPayload();
      if (!current || !Array.isArray(current.items)) return;

      dogs.forEach(function (dog) {
        /*
         * The dog's OWN item, found by its position in `_i` (`packIndex`), never by its
         * index in `dogs`. When `dogs` still dropped a dog that failed to load, pairing by
         * that index wrote the next dog's variant, plan and barcode into the dropped dog's
         * item, which kept the dropped dog's `_pet_*` and `_product_handle`.
         */
        var item = current.items[dog.packIndex];
        if (!dog.variant || !item) return;
        var isSub = purchaseType === 'subscription';
        var plan = isSub ? sellingPlanForCycle(dog.product, dog.cycle) : null;
        var freqWeeks = isSub ? parseInt(dog.cycle, 10) / 7 : 0;

        // Cart-line fields read by welcome-set.js when it builds /cart/add.js.
        item.id = dog.variant.id;
        item.selling_plan = plan;

        // Line-item properties. These must stay in lock-step with the variant
        // and plan the customer just re-selected, because Loop reads them as the
        // subscription's line attributes. petcon-api's build_item_properties is
        // the contract: _ongoing_plan_id is the VARIANT id, and all of these
        // live UNDER properties, so refresh the full set rather than a subset or
        // a stale line ships on a changed recipe/cycle.
        var props = item.properties || (item.properties = {});
        props._menu_barcode = dog.variant.barcode;
        props._shipping_interval_frequency = freqWeeks;
        props._shipping_interval_unit_type = 'Weeks';
        props._ongoing_plan_id = String(dog.variant.id);
        if (plan == null) {
          // One-time: no selling plan. Drop any plan id left over from a
          // previous subscription selection rather than ship a stale one.
          delete props._ongoing_plan_selling_plan_id;
        } else {
          props._ongoing_plan_selling_plan_id = String(plan);
        }

        // Earlier code wrote _ongoing_plan_id at the item top level, where
        // nothing reads it; drop it so it cannot be mistaken for the real value.
        delete item._ongoing_plan_id;
      });

      writePayload(current);
    }

    /*
     * One dog's three figures. Both discounts come off the full price and they never
     * combine:
     *
     *   full   the list price
     *   first  full less 30 % — the first order only
     *   later  full less 10 % — every delivery after it
     *
     * Einzelkauf gets neither, so it pays `full`. The two percentages are the
     * page's own (`[data-fresh-discounts]`), so this card and the single-dog card
     * cannot drift apart.
     *
     * Rounded, not truncated: `Math.round` reproduces the single-dog card's
     * rendered figures exactly (18481 -> 129,37 and 166,33; 10066 -> 70,46 and
     * 90,59).
     */
    function priceOf(dog) {
      if (!dog.variant) return null;
      var pct = discounts();
      var full = dog.variant.price || 0;
      return {
        full: full,
        first: Math.round(full * ((100 - pct.first) / 100)),
        later: Math.round(full * ((100 - pct.renewal) / 100)),
      };
    }
    /** What this dog costs per day, in cents, for the open purchase type. */
    function perDayOf(dog, which) {
      var money = priceOf(dog);
      var days = parseInt(dog.cycle, 10);
      if (!money || !days) return 0;
      return money[which] / days;
    }

    /*
     * "3,03 € pro Tag", in the page's own per-day copy. Rounded, not truncated, like the
     * single-dog card's rates (truncating only ever reads low). Empty when there is no
     * rate, or when the copy has no {price} to put it in, as the single-dog card skips
     * its "danach" line then.
     */
    function perDayText(key, cents) {
      var template = String(config().strings[key] || '');
      if (!(cents > 0) || !/\{price\}|\[price\]/.test(template)) return '';
      return t(key, { price: formatMoney(Math.round(cents)) });
    }

    /*
     * The pack's rate per day: every dog's own rate summed, then rounded once. The one
     * figure the Kaufart cards and the summary both show.
     */
    function packPerDay(which) {
      return Math.round(
        dogs.reduce(function (acc, dog) {
          return acc + perDayOf(dog, which);
        }, 0),
      );
    }

    /*
     * The pack's totals, for the summary, the sheet, the bar and the live region.
     * `due` is what the first delivery costs, `later` every delivery after it summed
     * over the dogs (each on its own cadence, which the disclaimer says).
     */
    function totals() {
      var isSub = purchaseType === 'subscription';
      var sum = { isSub: isSub, due: 0, full: 0, later: 0, priced: 0 };
      dogs.forEach(function (dog) {
        var money = priceOf(dog);
        if (!money) return;
        sum.priced++;
        sum.full += money.full;
        // Einzelkauf is not discounted, so there is nothing to strike through.
        sum.due += isSub ? money.first : money.full;
        sum.later += money.later;
      });
      return sum;
    }

    function recipeCount(dog) {
      var groups = dog.variant ? recipeGroups(dog.variant.barcode).length : 0;
      return groups || dog.recipes.length;
    }

    // ── the dog cards ────────────────────────────────────────────────────────
    var cards = [];
    var openIndex = -1;
    var sameState = []; // per dog: { copied, snapshot } for "Gleich wie"
    var customerChanged = false; // announce the totals only after the customer acted

    function customerEdited(index) {
      customerChanged = true;
      if (sameState[index]) {
        sameState[index].copied = false;
        sameState[index].snapshot = null;
      }
    }

    /*
     * Built once per dog, then patched in place on every change, so the radio the
     * customer just used keeps its focus (rebuilding the panel on each change, as the
     * tab layout did, dropped focus to <body> after every selection).
     */
    function createCard(dog, index) {
      var root = clone('dog');
      var id = 'fresh-pack-dog-' + index;
      var card = {
        index: index,
        root: root,
        toggle: hook(root, 'dog-toggle'),
        body: hook(root, 'dog-body'),
        summary: hook(root, 'dog-summary'),
        now: hook(root, 'dog-now'),
        later: hook(root, 'dog-later'),
        warn: hook(root, 'dog-warn'),
        daily: hook(root, 'dog-daily'),
        kcal: hook(root, 'dog-kcal'),
        perday: hook(root, 'dog-perday'),
        perdayNow: hook(root, 'dog-perday-now'),
        perdayLater: hook(root, 'dog-perday-later'),
        pensionStep: hook(root, 'dog-pension'),
        cycleStep: hook(root, 'dog-cycle'),
        cycleGroup: hook(root, 'dog-cycles'),
        cycleNotice: hook(root, 'dog-cycle-notice'),
        tiles: hook(root, 'dog-tiles'),
        variantNotice: hook(root, 'dog-variant-notice'),
        pensions: {},
        cycleKey: null,
        tilesKey: null,
        refocusCycle: false,
        same: null,
      };

      // WAI-ARIA accordion wiring.
      card.toggle.id = id + '-toggle';
      card.body.id = id + '-panel';
      card.toggle.setAttribute('aria-controls', card.body.id);
      card.body.setAttribute('aria-labelledby', card.toggle.id);
      card.toggle.addEventListener('click', function () {
        toggleDog(index);
      });

      hook(root, 'dog-avatar').classList.add(toneClass(dog.packIndex));
      hook(root, 'dog-name').textContent = dog.name;
      card.tiles.setAttribute('aria-label', t('packageRegion', { name: dog.name }));

      /*
       * The calorie need, per dog. The page heading printed one figure for the product
       * it was rendered from, which with a pack described whichever dog the buy chain
       * put in the URL; the figure lives here, where it can only mean this dog.
       */
      if (dog.kcal) {
        card.kcal.textContent = t('kcal', { kcal: dog.kcal });
        card.kcal.hidden = false;
      }

      hooks(root, 'pension-option').forEach(function (option) {
        var code = option.getAttribute('data-pack-pension-option');
        var input = option.querySelector('input');
        var label = option.querySelector('label');
        // Namespaced: the page's script queries [name="pension"] in this same form.
        input.id = id + '-pension-' + code;
        input.name = id + '-pension';
        label.setAttribute('for', input.id);
        var img = hook(option, 'pension-img');
        if (img && PENSION_IMG[code]) img.src = PENSION_IMG[code] + '&width=200';
        hooks(option, 'pet').forEach(function (node) {
          node.textContent = dog.name;
        });
        input.addEventListener('change', function () {
          if (!input.checked) return;
          customerEdited(index);
          handlers.onPension(index, code);
        });
        card.pensions[code] = { option: option, input: input };
      });

      if (index > 0) {
        var row = clone('same-as');
        var input = hook(row, 'same-input');
        input.id = id + '-same';
        input.addEventListener('change', function () {
          onSameAs(index, input.checked);
        });
        var slot = hook(root, 'dog-same');
        slot.appendChild(row);
        card.same = {
          slot: slot,
          input: input,
          title: hook(row, 'same-title'),
          preview: hook(row, 'same-preview'),
        };
      }

      return card;
    }

    function buildCards() {
      dogsHost.textContent = '';
      cards = dogs.map(function (dog, index) {
        var card = createCard(dog, index);
        dogsHost.appendChild(card.root);
        return card;
      });
    }

    function updateCard(card) {
      var dog = dogs[card.index];
      var isSub = purchaseType === 'subscription';
      var key = recipesKey(dog.recipes);
      var money = priceOf(dog);

      // Collapsed row: "Vollpension · alle 3 Wochen · 3 Sorten" and the dog's prices.
      card.summary.textContent = [
        pensionLabel(dog.pension),
        isSub ? t('every', { period: periodLabel(dog.cycle) }) : cycleDaysLabel(dog.cycle),
        recipesLabel(recipeCount(dog)),
      ].join(' · ');
      card.root.classList.toggle('is-attention', !money);
      card.warn.hidden = !!money;
      card.now.hidden = !money;
      card.later.hidden = !money || !isSub;
      if (money) {
        card.now.textContent = isSub
          ? t('priceNow', { price: formatMoney(money.first) })
          : formatMoney(money.full);
        card.later.textContent = isSub ? t('priceLater', { price: formatMoney(money.later) }) : '';
      }

      // Per day, under the kcal line: on the Sparplan the first box's rate and the later
      // one ("3,03 € pro Tag, danach 3,90 € pro Tag"), on Einzelkauf the one rate.
      var nowRate = money ? perDayText('perDay', perDayOf(dog, isSub ? 'first' : 'full')) : '';
      var laterRate = money && isSub ? perDayText('perDayAfter', perDayOf(dog, 'later')) : '';
      card.perdayNow.textContent = nowRate;
      card.perdayLater.textContent = nowRate && laterRate ? ', ' + laterRate : '';
      card.perday.hidden = !nowRate;
      card.daily.hidden = card.kcal.hidden && card.perday.hidden;

      // Portion: a pension this mix is not sold at is hidden, not rendered dead.
      var offered = pensionsForSellable(dog.product, key, dog.availability);
      Object.keys(card.pensions).forEach(function (code) {
        var entry = card.pensions[code];
        entry.option.hidden = offered.indexOf(code) === -1;
        entry.input.checked = dog.pension === code;
      });
      card.pensionStep.setAttribute('data-visible-pensions', String(offered.length || 1));

      // Lieferung alle: Abo-Sparplan only, as for one dog.
      card.cycleStep.hidden = !isSub;
      if (isSub) renderCycles(card, dog, key);

      renderTiles(card, dog);
      updateSameAs(card);
    }

    /*
     * The cycle buttons, rebuilt only when the set on offer changes (a portion switch
     * can change it), otherwise only re-checked, so the arrow keys keep working inside
     * the group. Cycle and portion are per dog because they have to be: each dog is a
     * different kcal product and they do not share a variant grid (0474 sells
     * Vollpension at 21 and 35 days while 0874 and 0774 sell it at 14 and 28).
     */
    function renderCycles(card, dog, key) {
      var cycles = cyclesFor(dog.product, key, dog.pension, dog.availability);
      var signature = cycles.join(',');
      if (card.cycleKey !== signature) {
        card.refocusCycle = card.cycleGroup.contains(document.activeElement);
        card.cycleGroup.textContent = '';
        cycles.forEach(function (cyc) {
          var button = clone('cycle');
          var input = button.querySelector('input');
          hook(button, 'cycle-label').textContent = periodLabel(cyc);
          hook(button, 'cycle-sub').textContent = cycleDaysLabel(cyc);
          input.name = 'fresh-pack-dog-' + card.index + '-cycle';
          input.value = cyc;
          input.addEventListener('change', function () {
            if (!input.checked) return;
            customerEdited(card.index);
            handlers.onCycle(card.index, cyc);
          });
          card.cycleGroup.appendChild(button);
        });
        card.cycleGroup.setAttribute('data-visible-cycles', String(cycles.length));
        card.cycleKey = signature;
      }
      card.cycleGroup.hidden = !cycles.length;
      card.cycleNotice.hidden = cycles.length > 0;
      card.cycleGroup.querySelectorAll('input').forEach(function (input) {
        var on = input.value === String(dog.cycle);
        input.checked = on;
        input.parentNode.classList.toggle('loop-selling-plan-button--selected', on);
        if (on && card.refocusCycle) {
          card.refocusCycle = false;
          input.focus();
        }
      });
    }

    /*
     * Paket: one tile per recipe for the variant this dog will be sold. The per-recipe
     * duration comes from the dog's own product page; it is left out, not guessed, when
     * that page could not be read.
     */
    function renderTiles(card, dog) {
      var variantId = dog.variant ? String(dog.variant.id) : '';
      card.variantNotice.hidden = !!dog.variant;
      card.tiles.hidden = !dog.variant;
      if (card.tilesKey === variantId) return;
      card.tilesKey = variantId;
      card.tiles.textContent = '';
      if (!dog.variant) return;

      var dayMap = dog.days ? dog.days[variantId] : null;
      recipeGroups(dog.variant.barcode).forEach(function (group) {
        var tile = clone('tile');
        hook(tile, 'tile-img').src = RECIPE_META[group.code].img + '&width=160';
        hook(tile, 'tile-name').textContent = config().recipes[group.code] || RECIPE_META[group.code].brand;
        hook(tile, 'tile-qty').textContent = group.sachets.join(', ');
        var days = dayMap ? parseInt(dayMap[group.code], 10) : 0;
        if (days > 0) {
          hook(tile, 'tile-days-value').textContent = days + ' ' + t('daysSuffix');
          hook(tile, 'tile-days').hidden = false;
        }
        card.tiles.appendChild(tile);
      });
    }

    // ── "Gleich wie {Name}" ──────────────────────────────────────────────────
    /*
     * A thin wrapper over the dog's own setters. Offered only when this dog's product
     * sells the previous dog's portion at the previous dog's cycle, so a tick always
     * produces exactly what it promises; not on Einzelkauf, where there is no cycle and
     * the Portion cards are locked by the form's one-time state.
     */
    function canCopy(from, to) {
      if (!from || !to || !from.variant || purchaseType !== 'subscription') return false;
      var key = recipesKey(to.recipes);
      if (pensionsForSellable(to.product, key, to.availability).indexOf(from.pension) === -1) return false;
      return cyclesFor(to.product, key, from.pension, to.availability).indexOf(String(from.cycle)) !== -1;
    }

    function sameChoice(a, b) {
      return a.pension === b.pension && String(a.cycle) === String(b.cycle);
    }

    function updateSameAs(card) {
      if (!card.same) return;
      var dog = dogs[card.index];
      var previous = dogs[card.index - 1];
      var offer = canCopy(previous, dog);
      card.same.slot.hidden = !offer;
      if (!offer) return;
      var state = sameState[card.index];
      card.same.title.textContent = t('sameAs', { name: previous.name });
      card.same.preview.textContent =
        pensionLabel(previous.pension) + ', ' + t('every', { period: periodLabel(previous.cycle) });
      // Ticked only while it is still true: copied in this visit and still the same.
      card.same.input.checked = !!(state && state.copied && sameChoice(previous, dog));
    }

    function onSameAs(index, checked) {
      var dog = dogs[index];
      var previous = dogs[index - 1];
      var state = sameState[index] || (sameState[index] = { copied: false, snapshot: null });
      customerChanged = true;

      if (checked && canCopy(previous, dog)) {
        state.snapshot = { pension: dog.pension, cycle: String(dog.cycle) };
        state.copied = true;
        // In the order the dog's own controls would be used: the portion first (which
        // pulls the cycle onto one it sells), then the cycle.
        handlers.onPension(index, previous.pension);
        handlers.onCycle(index, String(previous.cycle));
        return;
      }

      // Unticked: put back what this dog had before the copy.
      var snapshot = state.snapshot;
      state.copied = false;
      state.snapshot = null;
      if (!checked && snapshot) {
        handlers.onPension(index, snapshot.pension);
        handlers.onCycle(index, snapshot.cycle);
        return;
      }
      redraw();
    }

    // ── accordion ────────────────────────────────────────────────────────────
    function setOpen(card, open, instant) {
      card.root.classList.toggle('is-open', open);
      card.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!instant) return;
      card.root.classList.add('is-instant');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          card.root.classList.remove('is-instant');
        });
      });
    }

    /*
     * One card open at a time. When the open one is ABOVE the one being opened, it
     * collapses at once and the scroll position is corrected by its height, so the
     * header the customer just pressed stays exactly where it was; only the opening
     * card animates.
     */
    function toggleDog(index) {
      var target = cards[index];
      if (!target) return;
      var closing = openIndex === index;
      var previous = openIndex;
      var aboveCloses = previous !== -1 && previous < index;
      var before = target.toggle.getBoundingClientRect().top;

      if (previous !== -1 && previous !== index) setOpen(cards[previous], false, aboveCloses);
      setOpen(target, !closing, false);
      openIndex = closing ? -1 : index;

      if (aboveCloses) {
        var shift = target.toggle.getBoundingClientRect().top - before;
        if (Math.abs(shift) > 1) scrollByInstant(shift);
      }
    }

    /*
     * Which card opens first: a dog that cannot be sold as selected, else one whose
     * selection had to be moved off what the configurator sent (its `_i` variant was
     * not sellable), else the first dog.
     */
    function attentionIndex() {
      for (var i = 0; i < dogs.length; i++) {
        if (!dogs[i].variant) return i;
      }
      for (var j = 0; j < dogs.length; j++) {
        if (String(dogs[j].variant.id) !== String(dogs[j].item.id)) return j;
      }
      return 0;
    }

    // ── summary, Kaufart, bar ────────────────────────────────────────────────
    /*
     * THE SUMMARY, in the sticky column and in the sheet alike:
     *
     *   1. Every amount sits in a labelled column, and the "jetzt" column adds up to
     *      the headline above it.
     *   2. "Danach" is the sum of every dog's later delivery. The dogs renew on their
     *      own cadences, so it is not one charge — which is exactly what the
     *      disclaimer right under it says.
     */
    function renderSummary(root) {
      if (!root) return;
      var sum = totals();
      var pct = discounts();
      var reduced = sum.isSub && sum.full > sum.due;

      hook(root, 'sum-eyebrow').textContent = sum.isSub ? t('firstDelivery') : t('oneTimeTitle');
      hook(root, 'sum-now').textContent = sum.priced ? formatMoney(sum.due) : '—';
      hook(root, 'sum-compare').hidden = !reduced;
      hook(root, 'sum-full').textContent = formatMoney(sum.full);
      var saving = hook(root, 'sum-saving');
      saving.hidden = !reduced;
      saving.textContent = t('saving', { amount: formatMoney(sum.full - sum.due), percent: pct.first });

      // "= 5,43 € pro Tag" under the total: the pack's rate, the Kaufart card's figure.
      var dayRate = sum.priced ? perDayText('perDay', packPerDay(sum.isSub ? 'first' : 'full')) : '';
      hook(root, 'sum-perday-value').textContent = dayRate;
      hook(root, 'sum-perday').hidden = !dayRate;

      var table = hook(root, 'rows');
      table.classList.toggle('fresh-pack-rows--one-time', !sum.isSub);
      hook(root, 'col-now').textContent = sum.isSub ? t('colNow') : t('colPrice');
      var body = hook(root, 'rows-body');
      body.textContent = '';
      dogs.forEach(function (dog) {
        body.appendChild(summaryRow(dog, sum.isSub));
      });
      hook(root, 'rows-foot').hidden = !sum.isSub;
      hook(root, 'later-total').textContent = formatMoney(sum.later);
      // And what every later delivery comes to per day, under "Danach".
      var laterRate = sum.isSub && sum.priced ? perDayText('perDay', packPerDay('later')) : '';
      hook(root, 'later-perday-value').textContent = laterRate;
      hook(root, 'later-perday').hidden = !laterRate;

      var note = hook(root, 'sum-note');
      note.hidden = !sum.isSub;
      note.textContent = t('explain', { first: pct.first, renewal: pct.renewal });
    }

    function summaryRow(dog, isSub) {
      var row = clone('row');
      var money = priceOf(dog);
      hook(row, 'row-avatar').classList.add(toneClass(dog.packIndex));
      hook(row, 'row-name').textContent = dog.name;
      hook(row, 'row-meta').textContent = [
        pensionLabel(dog.pension),
        isSub ? t('every', { period: periodLabel(dog.cycle) }) : cycleDaysLabel(dog.cycle),
      ].join(' · ');
      var now = hook(row, 'row-now');
      var later = hook(row, 'row-later');
      if (!money) {
        now.textContent = t('unavailable');
        now.classList.add('fresh-pack-rows__warn');
        now.colSpan = isSub ? 2 : 1;
        later.parentNode.removeChild(later);
      } else {
        now.textContent = formatMoney(isSub ? money.first : money.full);
        later.textContent = formatMoney(money.later);
      }
      return row;
    }

    /*
     * The Kaufart cards' per-day rates, for the pack: the sum over the dogs, with the
     * scope line saying so. The same sums the summary uses (packPerDay).
     */
    function syncKaufartPrices() {
      ['first', 'later', 'full'].forEach(function (which) {
        var total = packPerDay(which);
        if (!(total > 0)) return;
        formEl.querySelectorAll('[data-pack-per-day="' + which + '"]').forEach(function (node) {
          node.textContent = formatMoney(total);
          node.classList.remove('fresh-pack-pending');
        });
      });
      formEl.querySelectorAll('[data-pack-scope]').forEach(function (node) {
        node.textContent = t('perDayScope', { count: dogs.length });
      });
    }

    /*
     * The bar's two lines, with the pack's totals. The follow-up line drops the
     * "alle {period}" the single-dog wording carries: each dog renews on its OWN
     * cadence, so there is no one period for the pack.
     */
    function syncSticky() {
      if (!barButton) return;
      var sum = totals();
      if (!(sum.due > 0)) return;
      var price = hook(barButton, 'bar-price');
      var info = hook(barButton, 'bar-info');
      if (sum.isSub) {
        price.textContent = t('stickySub', { price: formatMoney(sum.due) });
        info.textContent = t('stickyLater', { price: formatMoney(sum.later) });
      } else {
        price.textContent = t('stickyOneTime', { price: formatMoney(sum.full) });
        info.textContent = t('stickyOneTimeInfo');
      }
    }

    // The benefits belong to the Sparplan, as in the single-dog price card.
    var benefitsToggle = hook(layout, 'benefits-toggle');
    var benefitsToggleLabel = hook(layout, 'benefits-toggle-label');
    benefitsToggle.addEventListener('click', function () {
      var open = !benefitsWrap.classList.contains('is-open');
      benefitsWrap.classList.toggle('is-open', open);
      benefitsToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      benefitsToggleLabel.textContent = open ? t('benefitsHide') : t('benefitsShow');
    });

    function syncBenefits() {
      benefitsWrap.hidden = !(benefitsEl && purchaseType === 'subscription');
    }

    // One polite message with the two totals, after the customer changed something.
    var liveEl = hook(asideSummary, 'live');
    var liveTimer = 0;
    function announce() {
      if (!customerChanged || !liveEl) return;
      customerChanged = false;
      window.clearTimeout(liveTimer);
      // Worded when it fires, not now: "Gleich wie" changes two things in a row, and only
      // the end state is worth reading out.
      liveTimer = window.setTimeout(function () {
        var sum = totals();
        liveEl.textContent = sum.isSub
          ? t('liveSubscription', { now: formatMoney(sum.due), later: formatMoney(sum.later) })
          : t('liveOneTime', { total: formatMoney(sum.full) });
      }, 400);
    }

    // ── bottom sheet ─────────────────────────────────────────────────────────
    function createSheet() {
      var root = clone('sheet');
      if (!root) return null;
      var panel = hook(root, 'sheet-panel');
      var title = hook(root, 'sheet-title');
      root.id = 'fresh-pack-sheet';
      title.id = 'fresh-pack-sheet-title';
      panel.setAttribute('aria-labelledby', title.id);
      document.body.appendChild(root);
      var api = {
        root: root,
        panel: panel,
        summary: root.querySelector('.fresh-pack-summary'),
        open: false,
        opener: null,
        overflow: '',
      };
      hooks(root, 'sheet-close').forEach(function (node) {
        node.addEventListener('click', function () {
          closeSheet();
        });
      });
      if (barButton) barButton.setAttribute('aria-controls', root.id);
      return api;
    }

    var pageContainer = document.getElementById('PageContainer');

    function focusables(container) {
      return Array.prototype.filter.call(
        container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
        function (node) {
          return !node.disabled && node.getClientRects().length > 0;
        },
      );
    }

    function onSheetKeydown(event) {
      if (!sheet || !sheet.open) return;
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        event.stopPropagation();
        closeSheet();
        return;
      }
      if (event.key !== 'Tab') return;
      var nodes = focusables(sheet.panel);
      if (!nodes.length) {
        event.preventDefault();
        sheet.panel.focus();
        return;
      }
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      var active = document.activeElement;
      if (event.shiftKey && (active === first || active === sheet.panel || !sheet.panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !sheet.panel.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    function openSheet() {
      if (!sheet || sheet.open || !dogs.length) return;
      renderSummary(sheet.summary);
      sheet.open = true;
      sheet.opener = document.activeElement;
      sheet.overflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      if (pageContainer && 'inert' in pageContainer) pageContainer.inert = true;
      sheet.root.hidden = false;
      if (barButton) barButton.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', onSheetKeydown, true);
      // A frame later, so the slide-up has a start state to transition from.
      requestAnimationFrame(function () {
        if (sheet.open) sheet.root.classList.add('is-open');
      });
      // The panel itself (tabindex -1), so a screen reader starts at the dialog's title.
      sheet.panel.focus({ preventScroll: true });
    }

    function closeSheet() {
      if (!sheet || !sheet.open) return;
      sheet.open = false;
      sheet.root.classList.remove('is-open');
      document.removeEventListener('keydown', onSheetKeydown, true);
      document.body.style.overflow = sheet.overflow;
      if (pageContainer && 'inert' in pageContainer) pageContainer.inert = false;
      if (barButton) barButton.setAttribute('aria-expanded', 'false');
      var hide = function () {
        if (!sheet.open) sheet.root.hidden = true;
      };
      if (reducedMotion()) hide();
      else window.setTimeout(hide, 320);
      var back = sheet.opener && document.contains(sheet.opener) ? sheet.opener : barButton;
      if (back && typeof back.focus === 'function') back.focus();
    }

    if (barButton) barButton.addEventListener('click', openSheet);

    // The sheet is a phone and tablet surface; the summary is on screen from 992px.
    if (window.matchMedia) {
      var wideQuery = window.matchMedia('(min-width: 992px)');
      var onWide = function (event) {
        if (event.matches) closeSheet();
      };
      if (wideQuery.addEventListener) wideQuery.addEventListener('change', onWide);
      else if (wideQuery.addListener) wideQuery.addListener(onWide);
    }

    /*
     * The site header is `position: fixed` (Bootstrap's .fixed-top), so the sticky summary
     * has to stop below it, not at the top of the viewport. Its height is measured rather
     * than assumed: it differs per breakpoint and with the top bar's content.
     */
    var siteHeader = document.getElementById('shopify-section-standard-modern-header');
    function syncHeaderOffset() {
      if (!document.documentElement.classList.contains('fresh-pack')) return;
      var offset = 0;
      if (siteHeader) {
        var position = window.getComputedStyle(siteHeader).position;
        if (position === 'fixed' || position === 'sticky') offset = siteHeader.getBoundingClientRect().height;
      }
      document.documentElement.style.setProperty('--fresh-pack-header', Math.round(offset) + 'px');
    }
    syncHeaderOffset();
    if (siteHeader && 'ResizeObserver' in window) new ResizeObserver(syncHeaderOffset).observe(siteHeader);

    var resizeFrame = 0;
    window.addEventListener('resize', function () {
      if (resizeFrame) return;
      resizeFrame = requestAnimationFrame(function () {
        resizeFrame = 0;
        syncHeaderOffset();
      });
    });

    // ── draw ─────────────────────────────────────────────────────────────────
    var firstDraw = true;

    function redraw() {
      if (!dogs.length) return;
      liftCover();
      purchaseType = readPurchaseType();
      resolve();
      if (firstDraw) {
        firstDraw = false;
        buildCards();
        openIndex = attentionIndex();
        layout.classList.remove('is-loading');
        cards.forEach(function (card, i) {
          setOpen(card, i === openIndex, true);
        });
      }
      cards.forEach(updateCard);
      renderSummary(asideSummary);
      if (sheet && sheet.open) renderSummary(sheet.summary);
      syncBenefits();
      syncKaufartPrices();
      syncSticky();
      syncPayload();
      announce();
    }

    renderLoading();

    // ── load each dog's product ──────────────────────────────────────────────
    var pending = payload.items.map(function (item, index) {
      var handle = (item.properties && item.properties._product_handle) || item.product_handle;
      if (!handle) {
        console.warn('[multi-pet-plans] no _product_handle for item', index);
        return Promise.resolve(null);
      }
      /*
       * Two requests per dog, in parallel. The `.js` endpoint carries the variant
       * list and `selling_plan_groups`; the product PAGE carries the two things it
       * omits — the sellability map and the per-recipe durations, both of which
       * live in variant metafields.
       */
      var settled = Promise.all([
        fetch('/products/' + handle + '.js', { credentials: 'same-origin' }).then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status + ' for /products/' + handle + '.js');
          return r.json();
        }),
        loadPageData(handle, item.id),
      ])
        .then(function (results) {
          var product = results[0];
          var page = results[1];
          var initial = (product.variants || []).find(function (v) {
            return String(v.id) === String(item.id);
          });
          // The page's own figure when we could read it, else the handle's number as
          // a last resort — they differ (0474 prints 476), so the page wins.
          var kcalMatch = /(\d{3,4})\s*$/.exec(handle);
          return {
            product: product,
            item: item,
            // Its position in `_i` items: its own `_i` item (syncPayload) and its tint
            // (toneClass) are found by this, never by its index in `dogs`.
            packIndex: index,
            name: petName(item, index),
            kcal: page.kcal || (kcalMatch ? String(parseInt(kcalMatch[1], 10)) : null),
            recipes: recipesFromOption1(initial ? initial.option1 : 'CG'),
            pension: initial ? initial.option2 : 'V',
            cycle: initial ? String(initial.option3) : '28',
            variant: initial || null,
            availability: page.availability,
            days: page.days,
          };
        })
        .catch(function (err) {
          console.warn('[multi-pet-plans] failed to load', handle, err);
          return null;
        });
      // A request that never answers must not keep the page loading for good: past this
      // the dog counts as not loaded, and the customer gets the reload button.
      var timeout = new Promise(function (resolve) {
        window.setTimeout(function () {
          resolve(null);
        }, LOAD_TIMEOUT_MS);
      });
      return Promise.race([settled, timeout]);
    });

    Promise.all(pending).then(function (loaded) {
      var missing = [];
      loaded.forEach(function (dog, index) {
        if (!dog) missing.push(petName(payload.items[index], index));
      });
      if (missing.length) {
        console.warn('[multi-pet-plans] ' + missing.length + ' of ' + loaded.length + ' dogs did not load; order blocked');
        block(missing);
        return;
      }
      dogs = loaded;
      try {
        redraw();
        orderable = true;
      } catch (err) {
        // A pack that cannot be drawn cannot be ordered either: the same block, for all dogs.
        console.error('[multi-pet-plans] could not draw the pack; order blocked', err);
        block(
          loaded.map(function (dog) {
            return dog.name;
          }),
        );
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
